<script lang="ts">
  // Onboarding gate: pick the in-game display name. The world server sends LoginOk with
  // needsName=true (new players, or Discord/login users who never explicitly chose a name);
  // App routes here. On submit we send SetName; the server sanitises + persists, replies NameSet,
  // and App advances to required character creation. The name field is pre-seeded with the
  // suggested auth name.
  import { fly, scale } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import { session } from "../stores/session.svelte.ts";
  import { send } from "../stores/net.svelte.ts";
  import DataWindow from "../components/ui/DataWindow.svelte";
  import NeonButton from "../components/ui/NeonButton.svelte";
  import Field from "../components/ui/Field.svelte";

  const MAX = 20;
  let name = $state((session.loginOk?.avatar.name ?? "").slice(0, MAX));
  let busy = $state(false);

  const valid = $derived(name.trim().length >= 1);

  function submit() {
    if (!valid || busy) return;
    busy = true;
    send({ t: "SetName", name: name.trim() });
    // App's NameSet listener advances onboarding; if it somehow doesn't, re-enable so the
    // player isn't stuck on a dead button.
    setTimeout(() => (busy = false), 6000);
  }
</script>

<div class="absolute inset-0 z-10 flex items-center justify-center px-4">
  <div
    in:fly={{ y: 26, opacity: 0, duration: 560, easing: expoOut }}
    class="w-[min(440px,92vw)]"
  >
    <div in:scale={{ start: 0.94, opacity: 0, duration: 600, easing: expoOut }}>
      <DataWindow c={20}>
        <div class="flex items-center gap-2 border-b border-cyan-dim/40 bg-surface-2/70 px-5 py-2.5">
          <span
            class="size-1.5 rounded-full bg-cyan animate-pulse-soft"
            style="filter:drop-shadow(0 0 5px var(--color-cyan))"
          ></span>
          <span class="font-display text-[0.72rem] font-semibold uppercase tracking-[0.26em] text-cyan">
            Choose your name
          </span>
        </div>

        <div class="px-7 py-7 sm:px-8">
          <p class="mb-6 text-center text-sm leading-relaxed text-ink-dim">
            This is the name other duelists see in the world, in chat, and across the table.
            Make it a good one.
          </p>

          <Field
            id="ingame-name"
            bind:value={name}
            placeholder="Duelist name"
            autocomplete="off"
            maxlength={MAX}
            onkeydown={(e: KeyboardEvent) => e.key === "Enter" && submit()}
          />
          <div class="mt-1.5 text-right font-display text-xs uppercase tracking-[0.18em] text-ink-dim/75">
            {name.trim().length}/{MAX}
          </div>

          <div class="mt-4">
            <NeonButton full onclick={submit} disabled={!valid || busy}>
              {busy ? "Saving…" : "Continue"}
            </NeonButton>
          </div>
        </div>
      </DataWindow>
    </div>
  </div>
</div>
